import { Vehicle } from '../models/vehicle';
import {
  createPermissionsFromParams,
  generateIpfsSources,
  setVehiclePermissions,
  setVehiclePermissionsBulk,
} from '../services';
import {
  SetVehiclePermissions,
  SetVehiclePermissionsBulk,
} from '@dimo-network/transactions';
import { useDevCredentials } from '../context/DevCredentialsContext';
import { VehicleManagerMandatoryParams } from '../types';
import { useAuthContext } from '../context/AuthContext';
import { INVALID_SESSION_ERROR } from '../utils/authUtils';
import { generateAttachments } from '../services/permissionsService';

const shareSingleVehicle = async (tokenId: string, basePermissions: any) => {
  const vehiclePermissions: SetVehiclePermissions = {
    ...basePermissions,
    permissions: basePermissions.perms,
    tokenId: BigInt(tokenId),
  };
  await setVehiclePermissions(vehiclePermissions);
};
const shareMultipleVehicles = async (tokenIds: string[], basePermissions: any) => {
  const bulkVehiclePermissions: SetVehiclePermissionsBulk = {
    ...basePermissions,
    permissions: basePermissions.perms,
    tokenIds: tokenIds.map((id) => BigInt(id)),
  };
  await setVehiclePermissionsBulk(bulkVehiclePermissions);
};

const shareVehicles = async (tokenIds: string[], basePermissions: any) => {
  if (tokenIds.length === 1) {
    return shareSingleVehicle(tokenIds[0], basePermissions);
  }
  return shareMultipleVehicles(tokenIds, basePermissions);
};

interface Params {
  permissionTemplateId?: string;
  permissions?: string;
  clientId: `0x${string}` | null;
  expirationDate: BigInt;
  region?: string;
}

const getBasePermissions = async ({
  permissionTemplateId,
  permissions,
  clientId,
  expirationDate,
  region,
}: Params) => {
  const perms = createPermissionsFromParams(permissions, permissionTemplateId);
  console.log('useShareVehicles - region:', region);
  const attachments = generateAttachments(region?.toUpperCase());
  console.log('useShareVehicles - generated attachments:', attachments);
  const source = await generateIpfsSources(perms, clientId, expirationDate, attachments);
  return {
    grantee: clientId as `0x${string}`,
    perms,
    expiration: expirationDate,
    source,
  };
};

export const useShareVehicles = () => {
  const { clientId, expirationDate, permissionTemplateId, permissions, region } =
    useDevCredentials<VehicleManagerMandatoryParams>();
  const { validateSession } = useAuthContext();

  const validate = async () => {
    if (!clientId) {
      throw new Error('clientId is missing');
    }

    const permissionExists = permissionTemplateId || permissions;
    if (!permissionExists) {
      throw new Error('No permissions provided');
    }

    return !!(await validateSession());
  };

  return async (vehicles: Vehicle[]) => {
    if (!vehicles.length) {
      throw new Error('No vehicles shared');
    }
    const isValid = await validate();
    if (!isValid) throw new Error(INVALID_SESSION_ERROR);
    const tokenIds = vehicles.map((v) => v.tokenId.toString());
    const basePermissions = await getBasePermissions({
      clientId,
      permissionTemplateId,
      permissions,
      expirationDate,
      region,
    });
    return shareVehicles(tokenIds, basePermissions);
  };
};
